import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { useEffect, useState, useRef, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { ROUTE_PATHS, APP_VERSION, APP_STAGE, isAdminEmail } from "@/lib/index";
import { springPresets } from "@/lib/motion";
import { ambientEngine, musicSessionState } from "@/lib/ambientMusic";
import { useTheme, ThemeContext } from "@/hooks/useTheme";
import { ThemeSwitcher } from "@/components/ThemeSwitcher";

const INTERACTION_EVENTS = ['click', 'touchstart', 'keydown', 'scroll', 'mousemove'] as const;
import {
  BookOpen, Map, Palette, CreditCard, ChevronRight,
  Zap, Star, Gift, Coins, Sparkles, Check, Crown,
  Flame, Clock, Layers, X, Construction,
  Volume2, VolumeX, Eye, Heart, Phone, Menu, ShieldCheck
} from "lucide-react";

/* ══════════════════════════════════════════════════════════
   Easter Egg: nagrobki i zombie
══════════════════════════════════════════════════════════ */
const EASTER_CHARS = ['🪦','🧟','💀','🪦','🧟‍♀️','☠️','🪦','🧟','💀','🪦'] as const;

function GraveZombieEasterEgg({ onClose }: { onClose: () => void }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => { const t = setTimeout(() => setVisible(true), 30); return () => clearTimeout(t); }, []);
  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, scale: 0.85, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: -8 }}
          transition={{ duration: 0.35 }}
          className="fixed inset-0 z-[500] flex items-center justify-center"
          onClick={onClose}
        >
          <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.7)" }} />
          <div
            className="relative rounded-2xl p-8 max-w-sm w-full mx-4"
            style={{ background: "rgba(8,9,22,0.97)", border: "1px solid rgba(212,175,55,0.3)", boxShadow: "0 24px 60px rgba(0,0,0,0.7)" }}
            onClick={e => e.stopPropagation()}
          >
            <button onClick={onClose} className="absolute top-3 right-3 text-white/30 hover:text-white/60 transition-colors">
              <X className="w-4 h-4" />
            </button>
            <div className="text-5xl flex flex-wrap gap-2 justify-center mb-4">
              {EASTER_CHARS.map((c, i) => (
                <motion.span key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.07 }}
                >{c}</motion.span>
              ))}
            </div>
            <p className="text-center font-semibold mt-2" style={{ color: "#d4af37" }}>
              Znalazłeś ukryty plac cmentarzy! 🎉
            </p>
            <p className="text-center text-xs mt-2" style={{ color: "rgba(255,255,255,0.4)" }}>
              Na tym placu spoczywają wszyscy, którzy szukali wielokrotnie…
            </p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ══════════════════════════════════════════════════════════
   Stardust & Nebula canvas backgrounds
══════════════════════════════════════════════════════════ */
const HEADER_STYLE_KEY = 'egrob_header_style';

function StardustCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let animId: number;
    let W = canvas.width = canvas.offsetWidth;
    let H = canvas.height = canvas.offsetHeight;
    const stars = Array.from({ length: 120 }, () => ({
      x: Math.random() * W, y: Math.random() * H,
      r: Math.random() * 1.4 + 0.3,
      a: Math.random(),
      speed: Math.random() * 0.3 + 0.05,
      drift: (Math.random() - 0.5) * 0.2,
    }));
    function draw() {
      if (!ctx || !canvas) return;
      ctx.clearRect(0, 0, W, H);
      const grad = ctx.createLinearGradient(0, 0, W, H);
      grad.addColorStop(0, 'rgba(6,8,22,0.97)');
      grad.addColorStop(0.5, 'rgba(10,12,30,0.97)');
      grad.addColorStop(1, 'rgba(4,6,18,0.97)');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, W, H);
      for (const s of stars) {
        s.y -= s.speed;
        s.x += s.drift;
        s.a = 0.4 + 0.6 * Math.abs(Math.sin(Date.now() * 0.0008 + s.x));
        if (s.y < -2) { s.y = H + 2; s.x = Math.random() * W; }
        if (s.x < -2) s.x = W + 2;
        if (s.x > W + 2) s.x = -2;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(212,175,55,${s.a * 0.7})`;
        ctx.fill();
      }
      animId = requestAnimationFrame(draw);
    }
    draw();
    const ro = new ResizeObserver(() => {
      if (!canvas) return;
      W = canvas.width = canvas.offsetWidth;
      H = canvas.height = canvas.offsetHeight;
    });
    ro.observe(canvas);
    return () => { cancelAnimationFrame(animId); ro.disconnect(); };
  }, []);
  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'none' }} />;
}

function NebulaCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let animId: number;
    let W = canvas.width = canvas.offsetWidth;
    let H = canvas.height = canvas.offsetHeight;
    const particles = Array.from({ length: 80 }, () => ({
      x: Math.random() * W, y: Math.random() * H,
      vx: (Math.random() - 0.5) * 0.4, vy: (Math.random() - 0.5) * 0.4,
      r: Math.random() * 2 + 0.5,
      hue: Math.random() * 60 + 260,
      a: Math.random() * 0.6 + 0.2,
    }));
    let t = 0;
    function draw() {
      if (!ctx || !canvas) return;
      t++;
      ctx.clearRect(0, 0, W, H);
      const grad = ctx.createLinearGradient(0, 0, W, H);
      grad.addColorStop(0, 'rgba(10,6,30,0.97)');
      grad.addColorStop(0.5, 'rgba(16,8,40,0.97)');
      grad.addColorStop(1, 'rgba(6,4,22,0.97)');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, W, H);
      const nG = ctx.createRadialGradient(W * 0.6, H * 0.5, 0, W * 0.6, H * 0.5, W * 0.4);
      nG.addColorStop(0, `rgba(100,60,180,${0.12 + 0.04 * Math.sin(t * 0.02)})`);
      nG.addColorStop(1, 'transparent');
      ctx.fillStyle = nG;
      ctx.fillRect(0, 0, W, H);
      for (const p of particles) {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0) p.x = W; if (p.x > W) p.x = 0;
        if (p.y < 0) p.y = H; if (p.y > H) p.y = 0;
        const flicker = 0.3 + 0.7 * Math.abs(Math.sin(t * 0.01 + p.x));
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${p.hue},70%,75%,${p.a * flicker})`;
        ctx.fill();
      }
      animId = requestAnimationFrame(draw);
    }
    draw();
    const ro = new ResizeObserver(() => {
      if (!canvas) return;
      W = canvas.width = canvas.offsetWidth;
      H = canvas.height = canvas.offsetHeight;
    });
    ro.observe(canvas);
    return () => { cancelAnimationFrame(animId); ro.disconnect(); };
  }, []);
  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'none' }} />;
}

/* ══════════════════════════════════════════════════════════
   Helpers: session fingerprint
══════════════════════════════════════════════════════════ */
function getOrCreateSessionId(): string {
  const KEY = "egrob_sid";
  let sid = localStorage.getItem(KEY);
  if (!sid) {
    sid = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(KEY, sid);
  }
  return sid;
}

function formatVisits(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1).replace(/\.0$/, "")}k`;
  return String(n);
}

/* ══════════════════════════════════════════════════════════
   Baner: Licznik odwiedzin (prawy górny róg)
══════════════════════════════════════════════════════════ */
function VisitCounterBanner() {
  const [count, setCount]       = useState<number | null>(null);
  const [visible, setVisible]   = useState(true);
  const [isOwner, setIsOwner]   = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      // Sprawdź czy właściciel
      const { data: { user } } = await supabase.auth.getUser();
      const ownerEmails = ["arek@e-grob.pl"];
      const owner = !!(user && ownerEmails.includes(user.email ?? ""));
      if (!cancelled) setIsOwner(owner);

      // Pobierz session token
      const sessionId = getOrCreateSessionId();

      // Pobierz sesję auth jeśli istnieje (do nagłówka)
      const { data: { session } } = await supabase.auth.getSession();
      const authHeader = session?.access_token
        ? `Bearer ${session.access_token}`
        : undefined;

      try {
        const res = await supabase.functions.invoke("record-visit-2026-04-19", {
          body: { session_id: sessionId, path: window.location.pathname },
          headers: authHeader ? { Authorization: authHeader } : {},
        });
        if (!cancelled && res.data?.total != null) {
          setCount(res.data.total as number);
        }
      } catch {
        // cicha obsługa błędu
      }
    }

    init();
    return () => { cancelled = true; };
  }, []);

  if (!visible || count === null) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, x: 20, scale: 0.95 }}
        animate={{ opacity: 1, x: 0, scale: 1 }}
        exit={{ opacity: 0, x: 20, scale: 0.95 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1], delay: 1.2 }}
        className="fixed top-24 right-4 z-40"
      >
        <div
          className="relative flex items-center gap-0 rounded-2xl overflow-hidden"
          style={{
            background: "var(--theme-visits-bg)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            border: "1px solid var(--theme-visits-border)",
            boxShadow: "0 8px 32px rgba(0,0,0,0.5), 0 0 0 1px rgba(var(--theme-accent-rgb),0.05) inset",
          }}
        >
          {/* Złota kreska po lewej */}
          <div
            className="absolute left-0 top-0 bottom-0 w-0.5"
            style={{ background: "linear-gradient(to bottom, transparent, var(--theme-visits-accent), transparent)" }}
          />

          {/* Ikona + liczba — klikalne by rozwinąć */}
          <button
            onClick={() => setExpanded(e => !e)}
            className="flex items-center gap-2.5 px-3.5 py-2.5 transition-all duration-200 hover:bg-white/[0.03]"
            title="Odwiedziny strony"
          >
            {/* Ikona nagrobka miniatura */}
            <div className="relative flex-shrink-0">
              <svg width="16" height="22" viewBox="0 0 16 22" fill="none">
                <path
                  d="M2 21V9Q2 2 8 2Q14 2 14 9V21Z"
                  fill="var(--theme-card-bg-deep)"
                  stroke="var(--theme-visits-accent)"
                  strokeWidth="1"
                />
                <line x1="8" y1="5" x2="8" y2="11" stroke="var(--theme-visits-accent)" strokeWidth="1" opacity="0.85"/>
                <line x1="5" y1="7.5" x2="11" y2="7.5" stroke="var(--theme-visits-accent)" strokeWidth="1" opacity="0.85"/>
              </svg>
              {/* Pulsująca kropka */}
              <motion.div
                className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full"
                animate={{ opacity: [0.4, 1, 0.4], scale: [0.8, 1.2, 0.8] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                style={{ background: "var(--theme-visits-accent)", boxShadow: "0 0 4px var(--theme-visits-border)" }}
              />
            </div>

            {/* Licznik */}
            <div className="flex flex-col items-start">
              <motion.span
                key={count}
                initial={{ y: -8, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.35, ease: "easeOut" }}
                className="text-sm font-bold tabular-nums leading-none"
                style={{ fontFamily: "'Playfair Display', serif", color: "var(--theme-visits-accent)" }}
              >
                {formatVisits(count)}
              </motion.span>
              <span className="text-[9px] leading-none mt-0.5 uppercase tracking-wider" style={{ color: "var(--theme-text-dim)" }}>
                odwiedzin
              </span>
            </div>
          </button>

          {/* Rozwinięta wersja */}
          <AnimatePresence>
            {expanded && (
              <motion.div
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: "auto", opacity: 1 }}
                exit={{ width: 0, opacity: 0 }}
                transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                className="overflow-hidden"
              >
                <div
                  className="flex items-center gap-3 px-3 py-2.5"
                  style={{ borderLeft: "1px solid var(--theme-visits-border)" }}
                >
                  <Eye className="w-3 h-3 flex-shrink-0" style={{ color: "var(--theme-section-heading)" }} />
                  <div className="whitespace-nowrap">
                    <p className="text-[11px] font-medium" style={{ color: "var(--theme-text-bright)" }}>
                      {isOwner ? "Twoje wizyty nie są liczone" : "Unikalne sesje odwiedzin"}
                    </p>
                    <p className="text-[10px]" style={{ color: "var(--theme-text-dim)" }}>
                      od uruchomienia projektu
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Przycisk zamknięcia */}
          <button
            onClick={() => setVisible(false)}
            className="flex items-center justify-center w-7 h-full px-1.5 transition-all duration-200 hover:bg-white/10"
            style={{ borderLeft: "1px solid var(--theme-visits-border)", color: "var(--theme-text-dim)" }}
            aria-label="Zamknij"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

/* ══════════════════════════════════════════════════════════
   Baner: Strona w budowie
══════════════════════════════════════════════════════════ */
function UnderConstructionBanner() {
  const [visible, setVisible] = useState(true);

  if (!visible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="fixed bottom-5 left-1/2 -translate-x-1/2 z-40 pointer-events-none"
        style={{ width: "min(680px, calc(100vw - 2rem))" }}
      >
        <div
          className="pointer-events-auto flex items-center gap-3 px-5 py-3.5 rounded-2xl"
          style={{
            background: "var(--theme-banner-bg)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            border: "1px solid var(--theme-banner-border)",
            boxShadow: "0 10px 48px rgba(0,0,0,0.70), 0 0 0 1px rgba(var(--theme-accent-rgb),0.08) inset",
          }}
        >
          {/* Ikona + świeczka */}
          <div className="flex-shrink-0 relative">
            <motion.div
              animate={{ opacity: [0.6, 1, 0.7, 1, 0.8], scale: [1, 1.15, 0.95, 1.1, 1] }}
              transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut" }}
            >
              <Construction className="w-4 h-4" style={{ color: "var(--theme-section-heading)" }} />
            </motion.div>
          </div>

          {/* Złoty separator */}
          <div className="w-px h-5 flex-shrink-0" style={{ background: "var(--theme-banner-border)" }} />

          {/* Tekst */}
          <div className="flex-1 min-w-0">
            <span className="text-xs font-medium" style={{ color: "var(--theme-visits-accent)" }}>
              Strona w budowie
            </span>
            <span className="text-xs mx-2" style={{ color: "var(--theme-text-dim)" }}>·</span>
            <span className="text-xs" style={{ color: "var(--theme-text-muted)" }}>
              Pracujemy nad nową wersją. Możesz już teraz eksplorować dostępne funkcje.
            </span>
          </div>

          {/* Złoty pulsujący dot */}
          <motion.div
            className="flex-shrink-0 w-1.5 h-1.5 rounded-full"
            animate={{ opacity: [0.4, 1, 0.4], scale: [0.8, 1.2, 0.8] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            style={{ background: "var(--theme-visits-accent)", boxShadow: "0 0 6px var(--theme-visits-border)" }}
          />

          {/* Przycisk zamknięcia */}
          <button
            onClick={() => setVisible(false)}
            className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full transition-all duration-200 hover:bg-white/10"
            style={{ color: "var(--theme-text-dim)" }}
            aria-label="Zamknij"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

interface LayoutProps {
  children: React.ReactNode;
}

/* ══════════════════════════════════════════════════════════
   Mega-menu: JAK TO DZIAŁA
══════════════════════════════════════════════════════════ */
/* Kolory sekcji: indeks mapuje się na CSS var przez HOW_COLORS */
const HOW_SECTION_COLORS = ['heading', 'bright', 'muted', 'heading'] as const;
const HOW_SECTIONS = [
  {
    icon: BookOpen,
    title: "Cel projektu",
    colorKey: 'heading',
    items: [
      "Oswajanie lęku przed śmiercią przez tworzenie",
      "Cyfrowe miejsce pamięci dostępne dla bliskich",
      "Przestrzeń do zostawienia historii i wspomnień",
      "Inicjatywa o zdrowiu psychicznym i przemijaniu",
    ],
  },
  {
    icon: Map,
    title: "Proces rezerwacji",
    colorKey: 'bright',
    items: [
      "Wybierz wolne miejsce na Placu cyfrowym",
      "Zdecyduj o stylu nagrobka (9 wzorów)",
      "Wypełnij dane i wybierz plan subskrypcji",
      "Gotowe — Twoje miejsce czeka na kreację",
    ],
  },
  {
    icon: Palette,
    title: "Kreator e-grobu",
    colorKey: 'muted',
    items: [
      "Drag & drop — przeciągaj elementy na płótno",
      "Krzyże, kwiaty, tablice, rzeźby, znicze",
      "Motywy i skórki kamienia (klasyczny, onyx...)",
      "Dodawanie zdjęć i plików do 50 GB (Premium)",
    ],
  },
  {
    icon: CreditCard,
    title: "Plany i dostęp",
    colorKey: 'heading',
    items: [
      "Free — zawsze darmowy, 500 MB przestrzeni",
      "Podstawowy i Premium — wkrótce dostępne",
      "Bliscy mogą odwiedzać grób bez konta",
      "Plany płatne są aktualnie w przygotowaniu",
    ],
  },
];
function themeColor(key: typeof HOW_SECTION_COLORS[number]) {
  if (key === 'heading') return 'var(--theme-section-heading)';
  if (key === 'bright')  return 'var(--theme-text-bright)';
  return 'var(--theme-text-muted)';
}

/* ══════════════════════════════════════════════════════════
   Mega-menu: PLANY
══════════════════════════════════════════════════════════ */
type PlansTab = "plany" | "promocje" | "kredyty";

const PLANS_DATA = [
  {
    id: "free",
    name: "Free",
    price: "0 zł",
    period: "zawsze",
    color: "var(--theme-text-muted)",
    bg: "var(--theme-card-bg-deep)",
    border: "var(--theme-panel-inner-border)",
    features: ["500 MB przestrzeni", "1 nagrobek", "Styl klasyczny", "Dostęp dla bliskich"],
    cta: "Zacznij za darmo",
    icon: Flame,
    comingSoon: false,
  },
  {
    id: "podstawowy",
    name: "Podstawowy",
    price: "19 zł",
    period: "/ mies.",
    color: "var(--theme-text-dim)",
    bg: "var(--theme-card-bg-deep)",
    border: "var(--theme-panel-inner-border)",
    features: ["5 GB przestrzeni", "3 nagrobki", "5 stylów kamienia", "Elementy dekoracyjne", "Priorytetowe wsparcie"],
    cta: "Wkrótce dostępny",
    icon: Star,
    popular: false,
    comingSoon: true,
  },
  {
    id: "premium",
    name: "Premium",
    price: "49 zł",
    period: "/ mies.",
    color: "var(--theme-section-heading)",
    bg: "var(--theme-card-bg-deep)",
    border: "var(--theme-panel-border)",
    features: ["50 GB przestrzeni", "Nielimitowane nagrobki", "Wszystkie style i skórki", "Rzeźby i figury premium", "Galeria zdjęć HD", "API dla deweloperów"],
    cta: "Wkrótce dostępny",
    icon: Crown,
    popular: true,
    comingSoon: true,
  },
];

const PROMOS = [
  {
    icon: Gift,
    label: "Czarny Piątek",
    badge: "−40%",
    badgeColor: "rgba(220,80,80,0.9)",
    desc: "Premium przez 3 miesiące za 29 zł/mies.",
    expires: "Do 30 listopada 2026",
    code: "BLACK2026",
  },
  {
    icon: Sparkles,
    label: "Nowy użytkownik",
    badge: "1 mies. free",
    badgeColor: "rgba(100,180,220,0.9)",
    desc: "Pierwszy miesiąc Podstawowego gratis przy rejestracji.",
    expires: "Oferta stała",
    code: "WELCOME",
  },
  {
    icon: Clock,
    label: "Roczna subskrypcja",
    badge: "−20%",
    badgeColor: "rgba(140,200,120,0.9)",
    desc: "Zapłać za rok z góry i oszczędź 20% każdego miesiąca.",
    expires: "Oferta stała",
    code: "ANNUAL20",
  },
];

const CREDITS = [
  { icon: Layers, amount: "10 kredytów", price: "9 zł", desc: "Dodatkowe elementy dekoracyjne lub 1 GB przestrzeni" },
  { icon: Zap,    amount: "50 kredytów", price: "39 zł", desc: "Pakiet stylów premium + 5 GB przestrzeni", popular: true },
  { icon: Coins,  amount: "200 kredytów", price: "129 zł", desc: "Pełen pakiet — wszystkie elementy + 20 GB przestrzeni" },
];

/* ── animacja dropdownu ── */
const dropAnim = {
  initial: { opacity: 0, y: -8, scale: 0.98 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit:    { opacity: 0, y: -6, scale: 0.98 },
  transition: { duration: 0.2, ease: [0.22, 1, 0.36, 1] },
};

/* ── wspólny wrapper dropdownu ── */
function DropdownShell({ children, wide = false }: { children: React.ReactNode; wide?: boolean }) {
  return (
    <motion.div
      {...dropAnim}
      className="absolute top-full left-1/2 -translate-x-1/2 mt-2 rounded-2xl overflow-hidden z-50"
      style={{
        width: wide ? "min(860px, 96vw)" : "min(720px, 96vw)",
        background: "var(--theme-panel-bg)",
        border: "1px solid var(--theme-panel-border)",
        boxShadow: "0 32px 80px rgba(0,0,0,0.75), 0 0 0 1px rgba(var(--theme-accent-rgb),0.06)",
      }}
    >
      {children}
    </motion.div>
  );
}

/* ── Dropdown: Jak to działa ── */
function HowDropdown({ onClose }: { onClose: () => void }) {
  return (
    <DropdownShell>
      {/* nagłówek */}
      <div className="px-7 pt-6 pb-4 border-b" style={{ borderColor: "var(--theme-panel-inner-border)" }}>
        <p className="text-xs uppercase tracking-[0.3em]" style={{ color: "var(--theme-section-heading)" }}>przewodnik</p>
        <h3 className="text-lg font-semibold mt-0.5" style={{ fontFamily: "'Playfair Display', serif", color: "var(--theme-text-bright)" }}>
          Jak działa e-grób?
        </h3>
      </div>
      {/* siatka 2×2 */}
      <div className="grid grid-cols-2 gap-0">
        {HOW_SECTIONS.map((sec, i) => {
          const Icon = sec.icon;
          return (
            <div
              key={i}
              className="px-6 py-5 border-b border-r group hover:bg-white/[0.02] transition-colors duration-150"
              style={{
                borderColor: "var(--theme-panel-inner-border)",
                borderRight: i % 2 === 0 ? `1px solid var(--theme-panel-inner-border)` : "none",
                borderBottom: i < 2 ? `1px solid var(--theme-panel-inner-border)` : "none",
              }}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: "var(--theme-card-bg-medium)", border: "1px solid var(--theme-panel-border)" }}>
                  <Icon className="w-3.5 h-3.5" style={{ color: themeColor(sec.colorKey as typeof HOW_SECTION_COLORS[number]) }} />
                </div>
                <span className="text-sm font-medium" style={{ color: "var(--theme-text-bright)" }}>{sec.title}</span>
              </div>
              <ul className="space-y-1.5">
                {sec.items.map((item, j) => (
                  <li key={j} className="flex items-start gap-2">
                    <div className="w-1 h-1 rounded-full mt-1.5 flex-shrink-0" style={{ background: themeColor(sec.colorKey as typeof HOW_SECTION_COLORS[number]), opacity: 0.6 }} />
                    <span className="text-xs leading-relaxed" style={{ color: "var(--theme-text-muted)" }}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
      {/* stopka */}
      <div className="px-7 py-4 flex items-center justify-between" style={{ background: "var(--theme-panel-header-bg)" }}>
        <span className="text-xs" style={{ color: "var(--theme-text-dim)" }}>
          Masz pytania? Napisz do nas.
        </span>
        <Link
          to={ROUTE_PATHS.CMENTARZ}
          onClick={onClose}
          className="flex items-center gap-1.5 text-xs font-medium transition-opacity hover:opacity-80"
          style={{ color: "var(--theme-visits-accent)" }}
        >
          Przejdź do Placu cyfrowego <ChevronRight className="w-3 h-3" />
        </Link>
      </div>
    </DropdownShell>
  );
}

/* ── Dropdown: Plany ── */
function PlansDropdown({ onClose }: { onClose: () => void }) {
  const [tab, setTab] = useState<PlansTab>("plany");

  const tabs: { id: PlansTab; label: string; icon: React.ElementType }[] = [
    { id: "plany",    label: "Plany",     icon: CreditCard },
    { id: "promocje", label: "Promocje",  icon: Gift },
    { id: "kredyty",  label: "Kredyty",   icon: Coins },
  ];

  return (
    <DropdownShell wide>
      {/* zakładki */}
      <div className="flex border-b" style={{ borderColor: "var(--theme-panel-inner-border)" }}>
        {tabs.map(t => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className="flex items-center gap-2 px-6 py-4 text-sm font-medium transition-all duration-150 relative"
              style={{ color: active ? "var(--theme-visits-accent)" : "var(--theme-text-dim)" }}
            >
              <Icon className="w-3.5 h-3.5" />
              {t.label}
              {active && (
                <motion.div layoutId="plans-tab-indicator"
                  className="absolute bottom-0 left-0 right-0 h-px"
                  style={{ background: "var(--theme-visits-accent)" }} />
              )}
            </button>
          );
        })}
      </div>

      {/* zawartość zakładek */}
      <AnimatePresence mode="wait">
        {tab === "plany" && (
          <motion.div key="plany" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
<div className="grid grid-cols-3 gap-0">
              {PLANS_DATA.map((plan, i) => {
                const Icon = plan.icon;
                return (
                  <div key={plan.id}
                    className="relative px-6 py-5 flex flex-col"
                    style={{
                      background: plan.bg,
                      borderRight: i < 2 ? `1px solid var(--theme-panel-inner-border)` : "none",
                      opacity: plan.comingSoon ? 0.55 : 1,
                      filter: plan.comingSoon ? "grayscale(0.4)" : "none",
                    }}
                  >
                    {/* Badge: Wkrótce / Popularny */}
                    {plan.comingSoon ? (
                      <div className="absolute top-3 right-3">
                        <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full font-medium"
                          style={{ background: "var(--theme-card-bg-deep)", color: "var(--theme-text-dim)", border: "1px solid var(--theme-panel-inner-border)" }}>
                          Wkrótce
                        </span>
                      </div>
                    ) : null}
                    <div className="flex items-center gap-2 mb-3">
                      <Icon className="w-4 h-4" style={{ color: plan.color }} />
                      <span className="text-sm font-semibold" style={{ color: plan.color }}>{plan.name}</span>
                    </div>
                    <div className="flex items-baseline gap-1 mb-4">
                      <span className="text-2xl font-bold" style={{ fontFamily: "'Playfair Display', serif", color: plan.comingSoon ? "var(--theme-text-dim)" : "var(--theme-text-bright)" }}>
                        {plan.price}
                      </span>
                      <span className="text-xs" style={{ color: "var(--theme-text-dim)" }}>{plan.period}</span>
                    </div>
                    <ul className="space-y-1.5 mb-5 flex-1">
                      {plan.features.map((f, j) => (
                        <li key={j} className="flex items-start gap-2">
                          <Check className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: plan.color, opacity: plan.comingSoon ? 0.4 : 0.7 }} />
                          <span className="text-xs leading-relaxed" style={{ color: plan.comingSoon ? "var(--theme-text-dim)" : "var(--theme-text-muted)" }}>{f}</span>
                        </li>
                      ))}
                    </ul>
                    {plan.comingSoon ? (
                      <div>
                        <div
                          className="w-full py-2 rounded-lg text-xs font-medium text-center cursor-not-allowed select-none"
                          style={{
                            background: "var(--theme-card-bg-deep)",
                            color: "var(--theme-text-dim)",
                            border: "1px solid var(--theme-panel-inner-border)",
                          }}
                        >
                          🔒 Funkcja w budowie
                        </div>
                        <p className="text-[10px] text-center mt-1.5" style={{ color: "var(--theme-text-dim)" }}>
                          Pracujemy nad tym planem
                        </p>
                      </div>
                    ) : (
                      <Link to={ROUTE_PATHS.CMENTARZ} onClick={onClose}>
                        <button
                          className="w-full py-2 rounded-lg text-xs font-medium transition-all duration-200 hover:brightness-110"
                          style={{
                            background: "var(--theme-card-bg-medium)",
                            color: plan.color,
                            border: `1px solid ${plan.border}`,
                          }}
                        >
                          {plan.cta}
                        </button>
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="px-6 py-3 text-xs text-center border-t" style={{ borderColor: "var(--theme-panel-inner-border)", color: "var(--theme-text-dim)" }}>
              Plan Free jest w pełni dostępny · Plany płatne w przygotowaniu
            </div>
          </motion.div>
        )}

        {tab === "promocje" && (
          <motion.div key="promocje" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
            <div className="p-6 space-y-3">
              {PROMOS.map((promo, i) => {
                const Icon = promo.icon;
                return (
                  <div key={i} className="flex items-start gap-4 p-4 rounded-xl transition-colors duration-150 hover:bg-white/[0.025]"
                    style={{ background: "var(--theme-card-bg-deep)", border: "1px solid var(--theme-panel-inner-border)" }}>
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: "var(--theme-card-bg-medium)" }}>
                      <Icon className="w-4 h-4" style={{ color: promo.badgeColor }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium" style={{ color: "var(--theme-text-bright)" }}>{promo.label}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded font-bold"
                          style={{ background: promo.badgeColor + "22", color: promo.badgeColor, border: `1px solid ${promo.badgeColor}44` }}>
                          {promo.badge}
                        </span>
                      </div>
                      <p className="text-xs mb-1.5" style={{ color: "var(--theme-text-muted)" }}>{promo.desc}</p>
                      <div className="flex items-center gap-4">
                        <span className="text-[10px]" style={{ color: "var(--theme-text-dim)" }}>{promo.expires}</span>
                        <span className="text-[10px] px-2 py-0.5 rounded font-mono"
                          style={{ background: "var(--theme-card-bg-medium)", color: "var(--theme-section-heading)", border: "1px solid var(--theme-panel-border)" }}>
                          {promo.code}
                        </span>
                      </div>
                    </div>
                    <button
                      className="text-xs px-3 py-1.5 rounded-lg flex-shrink-0 transition-all duration-200 hover:brightness-110"
                      style={{ background: "rgba(var(--theme-accent-rgb),0.15)", color: "var(--theme-visits-accent)", border: "1px solid rgba(var(--theme-accent-rgb),0.2)" }}
                    >
                      Użyj
                    </button>
                  </div>
                );
              })}
            </div>
            <div className="px-6 pb-4 text-xs text-center" style={{ color: "var(--theme-text-dim)" }}>
              Promocje łączą się z planami subskrypcyjnymi · Kod wprowadź przy zakupie
            </div>
          </motion.div>
        )}

        {tab === "kredyty" && (
          <motion.div key="kredyty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
            <div className="p-6">
              <p className="text-xs mb-5" style={{ color: "var(--theme-text-muted)", lineHeight: "1.7" }}>
                Kredyty to jednorazowe doładowania — kupujesz je bez subskrypcji i wydajesz kiedy chcesz na dodatkowe elementy, przestrzeń lub premium style.
              </p>
              <div className="grid grid-cols-3 gap-3">
                {CREDITS.map((cr, i) => {
                  const Icon = cr.icon;
                  return (
                    <div key={i} className="relative p-4 rounded-xl flex flex-col gap-3"
                      style={{
                        background: cr.popular ? "var(--theme-card-bg-medium)" : "var(--theme-card-bg-deep)",
                        border: `1px solid ${cr.popular ? "var(--theme-panel-border)" : "var(--theme-panel-inner-border)"}`,  
                      }}
                    >
                      {cr.popular && (
                        <div className="absolute -top-2 left-1/2 -translate-x-1/2">
                          <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full whitespace-nowrap"
                            style={{ background: "rgba(var(--theme-accent-rgb),0.18)", color: "var(--theme-visits-accent)", border: "1px solid rgba(var(--theme-accent-rgb),0.3)" }}>
                            Najlepszy wybór
                          </span>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4 flex-shrink-0" style={{ color: cr.popular ? "var(--theme-visits-accent)" : "var(--theme-text-muted)" }} />
                        <span className="text-sm font-bold" style={{ fontFamily: "'Playfair Display', serif", color: "var(--theme-text-bright)" }}>
                          {cr.amount}
                        </span>
                      </div>
                      <p className="text-[11px] leading-snug" style={{ color: "var(--theme-text-dim)" }}>{cr.desc}</p>
                      <div className="flex items-center justify-between mt-auto">
                        <span className="text-lg font-bold" style={{ color: cr.popular ? "var(--theme-visits-accent)" : "var(--theme-text-bright)" }}>
                          {cr.price}
                        </span>
                        <button
                          className="text-xs px-3 py-1.5 rounded-lg transition-all duration-200 hover:brightness-110"
                          style={{
                            background: cr.popular ? "var(--theme-visits-accent)" : "var(--theme-card-bg-medium)",
                            color: cr.popular ? "rgba(8,9,18,0.95)" : "var(--theme-text-muted)",
                            border: cr.popular ? "none" : `1px solid var(--theme-panel-border)`,
                          }}
                        >
                          Kup
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="text-[10px] mt-4 text-center" style={{ color: "var(--theme-text-dim)" }}>
                Kredyty nie wygasają · Możesz je dokupić w każdej chwili z poziomu swojego profilu
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </DropdownShell>
  );
}

/* ══════════════════════════════════════════════════════════
   Nawigacja główna
══════════════════════════════════════════════════════════ */
function NavItems() {
  const location = useLocation();
  const isCmentarz = location.pathname === ROUTE_PATHS.CMENTARZ;
  const isIdea = location.pathname === ROUTE_PATHS.IDEA;
  const isSubPage =
    isCmentarz || isIdea ||
    location.pathname.startsWith(ROUTE_PATHS.REZERWACJA) ||
    location.pathname.startsWith(ROUTE_PATHS.KREATOR);

  const [openMenu, setOpenMenu] = useState<"how" | "plans" | null>(null);
  const navRef = useRef<HTMLDivElement>(null);

  // Zamknij przy kliknięciu poza menu
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setOpenMenu(null);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  if (isSubPage) {
    return (
      <nav className="hidden md:flex items-center gap-8">
        <Link to={ROUTE_PATHS.HOME}
          className="text-sm hover:text-primary transition-colors duration-200"
          style={{ color: "var(--theme-text-muted)" }}>
          ← Strona główna
        </Link>
        <Link to={ROUTE_PATHS.IDEA}
          className="text-sm hover:text-primary transition-colors duration-200"
          style={{ color: isIdea ? "var(--theme-visits-accent)" : "var(--theme-text-muted)" }}>
          Idea
        </Link>
        <Link to={ROUTE_PATHS.CMENTARZ}
          className="text-sm hover:text-primary transition-colors duration-200"
          style={{ color: isCmentarz ? "var(--theme-visits-accent)" : "var(--theme-text-muted)" }}>
          Plac cyfrowy
        </Link>
      </nav>
    );
  }

  return (
    <div ref={navRef} className="hidden md:flex items-center gap-2 relative">
      {/* Idea */}
      <Link
        to={ROUTE_PATHS.IDEA}
        className="text-sm px-3 py-2 rounded-lg hover:text-primary transition-colors duration-200"
        style={{ color: "var(--theme-text-muted)" }}
      >
        Idea
      </Link>

      {/* Jak to działa — dropdown */}
      <div className="relative">
        <button
          onMouseEnter={() => setOpenMenu("how")}
          onClick={() => setOpenMenu(prev => prev === "how" ? null : "how")}
          className="text-sm px-3 py-2 rounded-lg transition-all duration-150 flex items-center gap-1"
          style={{ color: openMenu === "how" ? "var(--theme-visits-accent)" : "var(--theme-text-muted)" }}
        >
          Jak to działa
          <motion.span animate={{ rotate: openMenu === "how" ? 180 : 0 }} transition={{ duration: 0.2 }}>
            <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
              <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </motion.span>
        </button>
        <AnimatePresence>
          {openMenu === "how" && (
            <div onMouseLeave={() => setOpenMenu(null)}>
              <HowDropdown onClose={() => setOpenMenu(null)} />
            </div>
          )}
        </AnimatePresence>
      </div>

      {/* Plany — dropdown */}
      <div className="relative">
        <button
          onMouseEnter={() => setOpenMenu("plans")}
          onClick={() => setOpenMenu(prev => prev === "plans" ? null : "plans")}
          className="text-sm px-3 py-2 rounded-lg transition-all duration-150 flex items-center gap-1"
          style={{ color: openMenu === "plans" ? "var(--theme-visits-accent)" : "var(--theme-text-muted)" }}
        >
          Plany
          <motion.span animate={{ rotate: openMenu === "plans" ? 180 : 0 }} transition={{ duration: 0.2 }}>
            <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
              <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </motion.span>
        </button>
        <AnimatePresence>
          {openMenu === "plans" && (
            <div onMouseLeave={() => setOpenMenu(null)}>
              <PlansDropdown onClose={() => setOpenMenu(null)} />
            </div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   Przycisk muzyki
══════════════════════════════════════════════════════════ */
function MusicButton() {
  // Stan UI synchronizowany z singletonem — nie resetuje się przy
  // remontowaniu komponentu (zmiana podstrony)
  const [playing, setPlaying] = useState(() => ambientEngine.isRunning);
  const [hovered, setHovered] = useState(false);

  // Autostart przy PIERWSZEJ interakcji w całej sesji.
  // musicSessionState żyje w module — nie w React, więc przeżywa
  // każdy remount Layoutu przy nawigacji.
  useEffect(() => {
    // Jeśli użytkownik już ręcznie wyłączył — nie restartuj nigdy
    if (musicSessionState.userMuted) return;
    // Jeśli już wystartowała w tej sesji — nie dodawaj listenerów
    if (musicSessionState.hasAutoStarted) return;

    const startOnce = async () => {
      if (musicSessionState.hasAutoStarted) return;
      musicSessionState.hasAutoStarted = true;
      INTERACTION_EVENTS.forEach(ev => window.removeEventListener(ev, startOnce));
      await ambientEngine.start();
      setPlaying(true);
    };

    INTERACTION_EVENTS.forEach(ev =>
      window.addEventListener(ev, startOnce, { once: true, passive: true })
    );

    return () => {
      INTERACTION_EVENTS.forEach(ev => window.removeEventListener(ev, startOnce));
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = useCallback(async () => {
    if (playing) {
      ambientEngine.toggle(); // ustawia userMuted = true wewnątrz
      setPlaying(false);
    } else {
      await ambientEngine.start();
      musicSessionState.userMuted = false;
      setPlaying(true);
    }
  }, [playing]);

  return (
    <motion.button
      onClick={toggle}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title={playing ? "Wyłącz muzykę" : "Włącz muzykę ambientową"}
      whileHover={{ scale: 1.08 }}
      whileTap={{ scale: 0.92 }}
      transition={springPresets.snappy}
      className="relative w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300"
      style={{
        background: playing ? "rgba(var(--theme-accent-rgb),0.12)" : "rgba(255,255,255,0.04)",
        border: `1px solid ${ playing ? "rgba(var(--theme-accent-rgb),0.35)" : "rgba(255,255,255,0.08)" }`,
        boxShadow: playing ? `0 0 12px rgba(var(--theme-accent-rgb),0.15)` : "none",
      }}
    >
      <AnimatePresence mode="wait" initial={false}>
        {playing ? (
          <motion.span key="on"
            initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.7, opacity: 0 }} transition={{ duration: 0.18 }}>
            <Volume2 className="w-4 h-4" style={{ color: "var(--theme-visits-accent)" }} />
          </motion.span>
        ) : (
          <motion.span key="off"
            initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.7, opacity: 0 }} transition={{ duration: 0.18 }}>
            <VolumeX className="w-4 h-4" style={{ color: "var(--theme-text-dim)" }} />
          </motion.span>
        )}
      </AnimatePresence>

      {playing && (
        <div className="absolute inset-0 rounded-full" aria-hidden="true">
          {[1, 2, 3].map(i => (
            <motion.span
              key={i}
              className="absolute inset-0 rounded-full"
              style={{ border: "1px solid rgba(var(--theme-accent-rgb),0.22)" }}
              animate={{ scale: [1, 1.6 + i * 0.3], opacity: [0.5, 0] }}
              transition={{
                duration: 1.8 + i * 0.4,
                repeat: Infinity,
                delay: i * 0.55,
                ease: "easeOut",
              }}
            />
          ))}
        </div>
      )}

      <AnimatePresence>
        {hovered && (
          <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full mt-2 left-1/2 -translate-x-1/2 whitespace-nowrap px-2.5 py-1 rounded-lg text-[10px] pointer-events-none z-50"
            style={{
              background: "var(--theme-card-bg-medium)",
              border: "1px solid var(--theme-panel-border)",
              color: "var(--theme-text-muted)",
            }}
          >
            {playing ? "Wyłącz muzykę" : "Włącz muzykę"}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.button>
  );
}

/* ── Helper: pojedynczy link w mobile drawer ── */
function MobileNavLink({ to, label, emoji }: { to: string; label: string; emoji: string }) {
  const { pathname } = useLocation();
  const active = pathname === to || pathname.startsWith(to + '/');
  return (
    <Link
      to={to}
      className="flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200"
      style={{
        background: active ? "rgba(var(--theme-accent-rgb),0.08)" : "transparent",
        border: `1px solid ${ active ? "rgba(var(--theme-accent-rgb),0.18)" : "transparent" }`,
        color: active ? "var(--theme-visits-accent)" : "var(--theme-text-muted)",
      }}
    >
      <span className="text-base w-6 text-center flex-shrink-0">{emoji}</span>
      <span className="text-sm font-medium">{label}</span>
      {active && <span className="ml-auto w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: "var(--theme-visits-accent)" }} />}
    </Link>
  );
}

/* ══════════════════════════════════════════════════════════
   Layout
══════════════════════════════════════════════════════════ */
export function Layout({ children }: LayoutProps) {
  const themeCtx = useTheme();
  const [isScrolled, setIsScrolled] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [headerStyle, setHeaderStyle] = useState<'stardust' | 'nebula'>(
    () => (localStorage.getItem(HEADER_STYLE_KEY) as 'stardust' | 'nebula') || 'stardust'
  );
  const [easterEggOpen, setEasterEggOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      setUserEmail(data.user?.email ?? null);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUserEmail(session?.user?.email ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  const { scrollY } = useScroll();
  const headerBg = useTransform(
    scrollY,
    [0, 100],
    ['rgba(0,0,0,0)', 'rgba(0,0,0,0.01)']
  );

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleHeaderStyle = () => {
    const next = headerStyle === 'stardust' ? 'nebula' : 'stardust';
    setHeaderStyle(next);
    localStorage.setItem(HEADER_STYLE_KEY, next);
  };

  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  return (
    <ThemeContext.Provider value={themeCtx}>
    {easterEggOpen && <GraveZombieEasterEgg onClose={() => setEasterEggOpen(false)} />}
    <div className="min-h-screen bg-background text-foreground">
      <motion.header
        style={{ backgroundColor: headerBg }}
        className={`fixed top-0 left-0 right-0 z-[100] transition-shadow duration-300 overflow-hidden ${isScrolled ? 'shadow-[0_2px_20px_rgba(0,0,0,0.4)]' : ''}`}
      >
        {/* Canvas tło banera */}
        <div className="absolute inset-0">
          {headerStyle === 'stardust' ? <StardustCanvas /> : <NebulaCanvas />}
        </div>
        <div className="container mx-auto px-4 h-20 md:h-24 flex items-center justify-between relative z-10">

          {/* Logo + badge wersji + admin icon + easter egg */}
          <div className="flex items-center gap-2">
            {/* Admin icon - widoczny tylko dla adminów */}
            {isAdminEmail(userEmail) && (
              <button
                onClick={() => navigate('/admin')}
                title="Panel administratora"
                className="w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 hover:scale-110"
                style={{ background: "rgba(212,175,55,0.1)", border: "1px solid rgba(212,175,55,0.2)" }}
              >
                <ShieldCheck className="w-4 h-4" style={{ color: "#d4af37" }} />
              </button>
            )}

            <Link to={ROUTE_PATHS.HOME} className="flex items-center">
              <img
                src="/images/logo_new_transparent.png"
                alt="e-grób logo"
                className="h-14 md:h-18 w-auto object-contain"
                style={{ filter: 'drop-shadow(0 0 12px rgba(255,255,255,0.5))' }}
              />
            </Link>

            {/* Badge wersji */}
            <Link
              to={ROUTE_PATHS.WERSJE}
              title="Historia wydań"
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full transition-all duration-200 hover:brightness-125"
              style={{
                background: "rgba(var(--theme-accent-rgb),0.07)",
                border: "1px solid rgba(var(--theme-accent-rgb),0.18)",
              }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full animate-pulse"
                style={{ background: "var(--theme-visits-accent)" }}
              />
              <span
                className="text-[10px] font-mono font-medium tracking-wide leading-none"
                style={{ color: "var(--theme-section-heading)" }}
              >
                v{APP_VERSION}
              </span>
            </Link>

            {/* Easter egg: nagrobki i zombie */}
            <button
              onClick={() => setEasterEggOpen(true)}
              title=""
              aria-label="Easter egg"
              className="text-lg leading-none transition-all duration-200 hover:scale-110 select-none"
              style={{ opacity: 0.15, cursor: 'pointer' }}
            >
              🪦
            </button>
          </div>

          {/* Desktop nav — ukryte na mobile */}
          <NavItems />

          {/* Desktop: Audio + ThemeSwitcher + Nebula toggle + Auth + CTA */}
          <div className="hidden md:flex items-center gap-3">
            <MusicButton />
            <ThemeSwitcher />
            {/* Nebula/Stardust toggle - ukryty, dostępny przez easter egg */}
            <button
              onClick={toggleHeaderStyle}
              title={headerStyle === 'stardust' ? 'Stardust (kliknij aby zmienić)' : 'Nebula Glow (kliknij aby zmienić)'}
              className="w-7 h-7 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
              style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", fontSize: '14px' }}
            >
              {headerStyle === 'stardust' ? '✦' : '✧'}
            </button>
            {userEmail ? (
              <Link to={ROUTE_PATHS.KONTO}
                className="text-sm px-4 py-2 rounded-lg transition-all duration-200 hover:bg-white/5 flex items-center gap-2"
                style={{ border: "1px solid var(--theme-panel-border)", color: "var(--theme-visits-accent)" }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--theme-visits-accent)" }} />
                Moje konto
              </Link>
            ) : (
              <Link to={ROUTE_PATHS.LOGOWANIE}
                className="text-sm px-4 py-2 rounded-lg transition-all duration-200 hover:bg-white/5"
                style={{ border: "1px solid var(--theme-panel-inner-border)", color: "var(--theme-text-muted)" }}>
                Zaloguj się
              </Link>
            )}
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} transition={springPresets.snappy}>
              <Link
                to={ROUTE_PATHS.CMENTARZ}
                className="px-6 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 inline-block"
                style={{
                  background: "var(--theme-visits-accent)",
                  color: "rgba(8, 9, 18, 0.95)",
                  boxShadow: "0 2px 16px rgba(var(--theme-accent-rgb),0.18)",
                }}
              >
                Zarezerwuj miejsce
              </Link>
            </motion.div>
          </div>

          {/* Mobile: muzyka + ThemeSwitcher + hamburger */}
          <div className="flex md:hidden items-center gap-2">
            <MusicButton />
            <ThemeSwitcher />
            <motion.button
              onClick={() => setMobileOpen(o => !o)}
              whileTap={{ scale: 0.9 }}
              transition={springPresets.snappy}
              aria-label="Menu"
              className="w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-200"
              style={{
                background: mobileOpen ? "rgba(var(--theme-accent-rgb),0.12)" : "rgba(255,255,255,0.04)",
                border: `1px solid ${ mobileOpen ? "rgba(var(--theme-accent-rgb),0.3)" : "rgba(255,255,255,0.08)" }`,
              }}
            >
              <AnimatePresence mode="wait" initial={false}>
                {mobileOpen ? (
                  <motion.span key="close"
                    initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.18 }}>
                    <X className="w-4 h-4" style={{ color: "var(--theme-visits-accent)" }} />
                  </motion.span>
                ) : (
                  <motion.span key="open"
                    initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.18 }}>
                    <Menu className="w-4 h-4" style={{ color: "var(--theme-text-muted)" }} />
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.button>
          </div>
        </div>
      </motion.header>

      {/* Mobile Drawer — wysuwa się od prawej */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.22 }}
              className="fixed inset-0 z-40 md:hidden"
              style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
              onClick={() => setMobileOpen(false)}
            />

            {/* Szuflada */}
            <motion.div
              key="drawer"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 320, damping: 34 }}
              className="fixed top-0 right-0 bottom-0 z-50 w-72 md:hidden flex flex-col"
              style={{
                background: "var(--theme-panel-bg)",
                borderLeft: "1px solid rgba(var(--theme-accent-rgb),0.1)",
                boxShadow: "-24px 0 60px rgba(0,0,0,0.5)",
              }}
            >
              {/* Nagłówek szuflady */}
              <div className="flex items-center justify-between px-5 pt-5 pb-4"
                style={{ borderBottom: "1px solid rgba(var(--theme-accent-rgb),0.08)" }}>
                <Link to={ROUTE_PATHS.HOME} className="flex items-center gap-2" onClick={() => setMobileOpen(false)}>
                  <img
                    src="/images/logo_new_transparent.png"
                    alt="e-grób logo"
                    className="h-10 w-auto object-contain"
                    style={{ filter: 'drop-shadow(0 0 8px rgba(255,255,255,0.4))' }}
                  />
                </Link>
                <motion.button
                  onClick={() => setMobileOpen(false)}
                  whileTap={{ scale: 0.9 }}
                  className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.07)" }}
                >
                  <X className="w-4 h-4" style={{ color: "var(--theme-text-dim)" }} />
                </motion.button>
              </div>

              {/* Nawigacja */}
              <nav className="flex-1 overflow-y-auto px-4 py-5 space-y-1">
                <MobileNavLink to={ROUTE_PATHS.IDEA} label="Idea" emoji="💡" />
                <MobileNavLink to={ROUTE_PATHS.CMENTARZ} label="Plac cyfrowy" emoji="🏛️" />
                <MobileNavLink to={ROUTE_PATHS.KREATOR} label="Kreator grobów" emoji="✦" />
                <MobileNavLink to={ROUTE_PATHS.WERSJE} label="Historia wersji" emoji="📋" />
              </nav>

              {/* Separator */}
              <div className="mx-4 h-px" style={{ background: "rgba(var(--theme-accent-rgb),0.08)" }} />

              {/* Auth + CTA */}
              <div className="px-4 py-5 space-y-3">
                {userEmail ? (
                  <Link
                    to={ROUTE_PATHS.KONTO}
                    className="flex items-center gap-3 w-full px-4 py-3 rounded-xl transition-all duration-200 hover:brightness-110"
                    style={{
                      background: "rgba(var(--theme-accent-rgb),0.07)",
                      border: "1px solid rgba(var(--theme-accent-rgb),0.2)",
                      color: "var(--theme-visits-accent)",
                    }}
                  >
                    <span className="w-2 h-2 rounded-full flex-shrink-0 animate-pulse" style={{ background: "var(--theme-visits-accent)" }} />
                    <span className="text-sm font-medium">Moje konto</span>
                    <ChevronRight className="w-4 h-4 ml-auto opacity-50" />
                  </Link>
                ) : (
                  <Link
                    to={ROUTE_PATHS.LOGOWANIE}
                    className="flex items-center gap-3 w-full px-4 py-3 rounded-xl transition-all duration-200 hover:brightness-110"
                    style={{
                      background: "rgba(255,255,255,0.03)",
                      border: "1px solid var(--theme-panel-inner-border)",
                      color: "var(--theme-text-muted)",
                    }}
                  >
                    <span className="text-sm font-medium">Zaloguj się</span>
                    <ChevronRight className="w-4 h-4 ml-auto opacity-40" />
                  </Link>
                )}

                <Link
                  to={ROUTE_PATHS.CMENTARZ}
                  className="flex items-center justify-center gap-2 w-full px-4 py-3.5 rounded-xl text-sm font-semibold transition-all duration-200 active:scale-95"
                  style={{
                    background: "var(--theme-visits-accent)",
                    color: "rgba(8,9,18,0.95)",
                    boxShadow: "0 4px 20px rgba(var(--theme-accent-rgb),0.22)",
                  }}
                >
                  🪦 Zarezerwuj miejsce
                </Link>
              </div>

              {/* Stopka szuflady */}
              <div className="px-5 py-4" style={{ borderTop: "1px solid rgba(255,255,255,0.04)" }}>
                <p className="text-[10px] text-center" style={{ color: "var(--theme-text-dim)" }}>
                  v{APP_VERSION} · projekt humorystyczny i edukacyjny
                </p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Baner stagingowy */}
      <UnderConstructionBanner />
      {/* Licznik odwiedzin — prawy górny róg */}
      <VisitCounterBanner />
      <main className="pt-16 md:pt-20">{children}</main>

      <footer
        className="relative py-12"
        style={{ borderTop: "1px solid rgba(var(--theme-accent-rgb),0.12)", background: "var(--theme-footer-bg)" }}
      >
        {/* ── Charakter projektu + pomoc kryzysowa ── */}
        <div className="border-b pb-10 mb-10" style={{ borderColor: "var(--theme-panel-inner-border)" }}>
          <div className="container mx-auto px-4">
            <div
              className="flex flex-col md:flex-row items-stretch max-w-3xl mx-auto rounded-2xl overflow-hidden"
              style={{ border: "1px solid var(--theme-footer-card-border)", background: "var(--theme-footer-card-bg)" }}
            >
              {/* Lewa — charakter strony */}
              <div
                className="flex-1 px-7 py-6 flex flex-col justify-center md:border-r border-b md:border-b-0"
                style={{ borderColor: "var(--theme-panel-inner-border)" }}
              >
                <div className="flex items-center gap-2.5 mb-3">
                  <div
                    className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: "rgba(var(--theme-accent-rgb),0.08)", border: "1px solid rgba(var(--theme-accent-rgb),0.15)" }}
                  >
                    <span className="text-sm leading-none" role="img" aria-label="nagrobek">🪦</span>
                  </div>
                  <p className="text-[10px] uppercase tracking-[0.28em]" style={{ color: "var(--theme-section-heading)" }}>
                    o projekcie
                  </p>
                </div>
                <p className="text-sm leading-relaxed" style={{ color: "var(--theme-text-muted)" }}>
                  <span style={{ color: "var(--theme-text-bright)" }}>e-grób</span>{" "}
                  to projekt o charakterze{" "}
                  <span style={{ color: "var(--theme-section-heading)" }}>humorystycznym i edukacyjnym</span>
                  {" "}— oswajamy temat przemijania przez twórczość i refleksję.
                </p>
                <p className="text-xs mt-2.5 leading-relaxed" style={{ color: "var(--theme-text-dim)" }}>
                  Nie nakłaniamy do żadnych niebezpiecznych ani niemoralnych zachowań.
                  Śmierć jest częścią życia — rozmawiamy o niej z szacunkiem i lekkim przymrużeniem oka.
                </p>
              </div>

              {/* Prawa — pomoc kryzysowa */}
              <div
                className="flex-1 px-7 py-6 flex flex-col justify-center"
                style={{ background: "var(--theme-footer-help-bg)" }}
              >
                <div className="flex items-center gap-2.5 mb-3">
                  <div
                    className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: "var(--theme-help-bg)", border: "1px solid var(--theme-help-border)" }}
                  >
                    <Heart className="w-3.5 h-3.5" style={{ color: "var(--theme-help-color)" }} />
                  </div>
                  <p className="text-[10px] uppercase tracking-[0.28em]" style={{ color: "var(--theme-help-color)" }}>
                    jeśli potrzebujesz pomocy
                  </p>
                </div>
                <p className="text-sm leading-relaxed mb-4" style={{ color: "var(--theme-text-muted)" }}>
                  Jeśli przeżywasz trudny moment i potrzebujesz rozmowy —{" "}
                  <span style={{ color: "var(--theme-text-bright)" }}>jesteś ważny i nie musisz być sam.</span>
                </p>
                <div className="space-y-2">
                  <a
                    href="tel:116123"
                    className="flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200 hover:brightness-125 group"
                    style={{ background: "var(--theme-help-bg)", border: "1px solid var(--theme-help-border)" }}
                  >
                    <Phone className="w-3.5 h-3.5 flex-shrink-0 group-hover:scale-110 transition-transform duration-200" style={{ color: "var(--theme-help-color)" }} />
                    <div>
                      <span className="text-sm font-bold tracking-wider block" style={{ fontFamily: "'Playfair Display', serif", color: "var(--theme-help-color)" }}>
                        116 123
                      </span>
                      <span className="text-[10px]" style={{ color: "var(--theme-text-dim)" }}>
                        Telefon Zaufania · bezpłatny · całodobowy
                      </span>
                    </div>
                  </a>
                  <a
                    href="tel:800702222"
                    className="flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200 hover:brightness-125 group"
                    style={{ background: "rgba(var(--theme-accent-rgb),0.04)", border: "1px solid var(--theme-help-border)" }}
                  >
                    <Phone className="w-3.5 h-3.5 flex-shrink-0 group-hover:scale-110 transition-transform duration-200" style={{ color: "var(--theme-help-color)" }} />
                    <div>
                      <span className="text-sm font-bold tracking-wider block" style={{ fontFamily: "'Playfair Display', serif", color: "var(--theme-help-color)" }}>
                        800 70 2222
                      </span>
                      <span className="text-[10px]" style={{ color: "var(--theme-text-dim)" }}>
                        Centrum Wsparcia · bezpłatny · całodobowy
                      </span>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center gap-3 text-center">
            <div className="flex items-center gap-3">
              <img
                src="/images/logo_new_transparent.png"
                alt="e-grób logo"
                className="h-12 w-auto object-contain"
                style={{ filter: 'drop-shadow(0 0 8px rgba(255,255,255,0.3))', opacity: 0.9 }}
              />
              <span className="text-2xl font-bold"
                style={{ fontFamily: "'Playfair Display', serif", color: "var(--theme-section-heading)" }}>
                e-grób
              </span>
            </div>
            <p className="text-sm max-w-md" style={{ color: "var(--theme-text-muted)" }}>
              Cyfrowe miejsce pamięci. Zostaw coś po sobie.
            </p>
            <div className="w-12 h-px my-1"
              style={{ background: "linear-gradient(to right, transparent, var(--theme-visits-accent), transparent)", opacity: 0.4 }} />
            <div className="flex flex-wrap justify-center items-center gap-x-4 gap-y-1">
              <span className="text-xs" style={{ color: "var(--theme-text-dim)" }}>
                © 2026 e-grób. Wszelkie prawa zastrzeżone.
              </span>
              <span className="text-xs" style={{ color: "var(--theme-text-dim)" }}>·</span>
              <Link to={ROUTE_PATHS.POLITYKA}
                className="text-xs transition-all duration-200 hover:opacity-90"
                style={{ color: "var(--theme-text-muted)", textDecoration: "underline", textUnderlineOffset: "3px" }}>
                Polityka prywatności
              </Link>
              <span className="text-xs" style={{ color: "var(--theme-text-dim)" }}>·</span>
              <Link to={ROUTE_PATHS.REGULAMIN}
                className="text-xs transition-all duration-200 hover:opacity-90"
                style={{ color: "var(--theme-text-muted)", textDecoration: "underline", textUnderlineOffset: "3px" }}>
                Regulamin
              </Link>
            </div>
            {/* Badge wersji w stopce */}
            <Link
              to={ROUTE_PATHS.WERSJE}
              className="inline-flex items-center gap-2 mt-1 px-3 py-1.5 rounded-full transition-all duration-200 hover:brightness-110"
              style={{
                background: "rgba(var(--theme-accent-rgb),0.06)",
                border: "1px solid rgba(var(--theme-accent-rgb),0.15)",
              }}
            >
              <span className="w-1.5 h-1.5 rounded-full animate-pulse"
                style={{ background: (APP_STAGE as string) === 'stable' ? 'rgba(100,200,120,0.8)' : 'rgba(220,150,80,0.8)' }} />
              <span className="text-[10px] font-mono font-medium tracking-wide"
                style={{ color: "var(--theme-section-heading)" }}>
                v{APP_VERSION}
              </span>
              <span className="text-[9px] uppercase tracking-widest"
                style={{ color: "var(--theme-text-dim)" }}>
                changelog
              </span>
            </Link>
          </div>
        </div>
      </footer>
    </div>
    </ThemeContext.Provider>
  );
}